// IMMEDIATE DEBUG LOG TO CHECK IF PLATFORM FILE IS LOADED
console.log('🔥🔥🔥 PLATFORM.TSX FILE LOADED!!!');

import React, { useState, useEffect } from 'react';
import Canvas from '../components/canvas/Canvas';
import Portfolio from './Portfolio';
import Wallets  from './Wallets';
import Settings from './Settings';
import Community from '../components/Community';
import { CanvasWidget } from '../components/canvas/types';

interface CanvasRef {
  addWidget: (widget: Partial<CanvasWidget>) => void;
}

interface PlatformProps {
  onGoHome: () => void;
  walletAddress: string;
  walletProvider?: any;
  onShowConnectModal: () => void;
  onDisconnectWallet?: () => void;
  currentView: 'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community';
  onViewChange: (view: 'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community') => void;
}

const Platform: React.FC<PlatformProps> = ({ 
  onGoHome, 
  walletAddress, 
  walletProvider,
  onShowConnectModal, 
  onDisconnectWallet,
  currentView,
  onViewChange
}) => {
  // IMMEDIATE DEBUG LOG TO CHECK IF PLATFORM COMPONENT IS RENDERED
  console.log('🔥🔥🔥 PLATFORM COMPONENT RENDERED!!!');
  console.log('🔥🔥🔥 Platform currentView:', currentView);
  console.log('🔥🔥🔥 Platform walletAddress:', walletAddress);
  
  const canvasRef = React.useRef<CanvasRef>(null);
  

  // Add effect to toggle trade-view-active class on the body
  useEffect(() => {
    if (currentView === 'trade') {
      document.body.classList.add('trade-view-active');
      // Add the hide-grid-background class to hide the default grid
      document.body.classList.add('hide-grid-background');
      // No longer hiding the green glow effects on the canvas page
    } else {
      document.body.classList.remove('trade-view-active');
      // Remove the class when not in trade view
      document.body.classList.remove('hide-grid-background');
    }
    
    // Clean up on unmount
    return () => {
      document.body.classList.remove('trade-view-active');
      document.body.classList.remove('hide-grid-background');
    };
  }, [currentView]);

  const handleAddWidget = (widget: Partial<CanvasWidget>) => {
    canvasRef.current?.addWidget(widget);
  };

  const renderView = () => {
    console.log('🔥🔥🔥 Platform renderView called with currentView:', currentView);
    
    switch (currentView) {
      case 'trade':
        console.log('🔥🔥🔥 Platform rendering Canvas component');
        return <Canvas ref={canvasRef} />;
      case 'portfolio':
        console.log('🔥🔥🔥 Platform rendering Portfolio component');
        return <Portfolio walletAddress={walletAddress} onShowConnectModal={onShowConnectModal} />;
      case 'wallets':
        console.log('🔥🔥🔥 Platform rendering Wallets component');
        return <Wallets walletAddress={walletAddress} onShowConnectModal={onShowConnectModal} />;
      case 'settings':
        console.log('🔥🔥🔥 Platform rendering Settings component');
        return <Settings />;
      case 'community':
        console.log('🔥🔥🔥 Platform rendering Community component');
        return <Community />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-14 relative overflow-hidden">
      {/* Enhanced Background with Green Radials and Design Elements - FIXED, NO SCROLLING */}
      <div className="fixed inset-0 overflow-hidden z-0 pointer-events-none">
        {/* Base gradients */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#24c35b]/15 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-[#24c35b]/15 via-transparent to-transparent"></div>
        
        {/* Main Green Glows - FIXED */}
        <div className="absolute top-1/4 -left-[10%] w-[500px] h-[500px] rounded-full bg-[#24c35b]/25 blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-[10%] w-[600px] h-[600px] rounded-full bg-[#24c35b]/20 blur-[120px]"></div>
        
        {/* Dark Slate Gray Accents - FIXED */}
        <div className="absolute top-1/3 left-1/2 w-[300px] h-[300px] rounded-full bg-slate-700/15 blur-[80px]"></div>
        <div className="absolute bottom-1/3 right-1/2 w-[250px] h-[250px] rounded-full bg-slate-600/10 blur-[60px]"></div>
        
        {/* Large Green Radial Backgrounds - FIXED */}
        <div className="absolute top-32 left-16 w-[600px] h-[600px] bg-[#24c35b]/18 rounded-full blur-[80px] animate-pulse"></div>
        <div className="absolute top-1/2 right-32 w-[550px] h-[550px] bg-[#2dd968]/20 rounded-full blur-[70px]"></div>
        <div className="absolute bottom-32 left-1/3 w-[620px] h-[620px] bg-[#24c35b]/16 rounded-full blur-[90px]"></div>
        <div className="absolute top-1/3 right-1/4 w-[480px] h-[480px] bg-[#2dd968]/18 rounded-full blur-[60px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-16 w-[580px] h-[580px] bg-[#24c35b]/17 rounded-full blur-[75px]"></div>
        
        {/* Additional scattered elements - FIXED */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#24c35b]/15 rounded-full blur-2xl"></div>
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[#24c35b]/12 rounded-full blur-xl"></div>
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-[#24c35b]/14 rounded-full blur-2xl"></div>
        
        {/* Geometric Design Elements - FIXED */}
        <div className="absolute top-20 right-1/4 w-32 h-32 border border-slate-700/20 rounded-lg rotate-45 animate-pulse"></div>
        <div className="absolute bottom-40 left-20 w-24 h-24 border border-[#24c35b]/30 rounded-full"></div>
        <div className="absolute top-1/2 left-10 w-16 h-16 bg-slate-600/10 rounded rotate-12"></div>
        <div className="absolute top-2/3 right-1/3 w-20 h-20 border border-slate-600/15 rounded-lg rotate-12"></div>
        <div className="absolute bottom-1/3 left-1/4 w-18 h-18 bg-[#24c35b]/20 rounded-full blur-sm"></div>
        
        {/* Subtle grid pattern - FIXED */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #24c35b 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}></div>
      </div>

      {/* BOLD BACKGROUND TEXT FOR DEPTH - Glass-like, Straight, TRULY FIXED (hidden during trade view) */}
      <div className={`transition-opacity duration-500 ${currentView === 'trade' ? 'opacity-0' : 'opacity-100'}`} style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 5, 
        pointerEvents: 'none', 
        overflow: 'hidden' 
      }}>
        {/* Portfolio View */}
        <div style={{ 
          position: 'absolute', 
          top: '25%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          fontSize: '280px', 
          fontWeight: '900', 
          lineHeight: '1', 
          letterSpacing: '-0.025em', 
          userSelect: 'none',
          color: 'rgba(36, 195, 91, 0.05)' 
        }}>
          PORTFOLIO
        </div>
        
        {/* Wallets View */}
        <div style={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          fontSize: '260px', 
          fontWeight: '900', 
          lineHeight: '1', 
          letterSpacing: '-0.025em', 
          userSelect: 'none',
          color: 'rgba(71, 85, 105, 0.05)' 
        }}>
          WALLETS
        </div>
        
        {/* Settings View */}
        <div style={{ 
          position: 'absolute', 
          top: '75%', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          fontSize: '240px', 
          fontWeight: '900', 
          lineHeight: '1', 
          letterSpacing: '-0.025em', 
          userSelect: 'none',
          color: 'rgba(36, 195, 91, 0.05)' 
        }}>
          SETTINGS
        </div>
      </div>
      
      <main className="flex-1 overflow-hidden relative z-10">
        {renderView()}
      </main>
    </div>
  );
}

export default Platform;